import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { useUser } from "@/contexts/UserContext";

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;
const SW_PATH = "/sw.js";

export type NotifPermission = "default" | "granted" | "denied" | "unsupported";

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

// ── Local browser notification (works in foreground + background tabs) ─────────
export function showLocalNotification(title: string, body: string, url = "/account?tab=messages") {
  if (Notification.permission !== "granted") return;
  try {
    const n = new Notification(title, {
      body,
      icon: "/favicon.ico",
      tag: "bg360-msg",
      renotify: true,
    });
    n.onclick = () => {
      window.focus();
      window.location.pathname = url;
      n.close();
    };
  } catch {
    // Safari / restricted environments
  }
}

// ── Hook ──────────────────────────────────────────────────────────────────────
export function usePushNotifications() {
  const { user } = useUser();
  const [permission, setPermission] = useState<NotifPermission>(() => {
    if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
    return Notification.permission as NotifPermission;
  });
  const [swReady, setSwReady] = useState(false);
  const [subscribing, setSubscribing] = useState(false);

  const isSupported =
    typeof window !== "undefined" &&
    "Notification" in window &&
    "serviceWorker" in navigator &&
    "PushManager" in window;

  // Register service worker
  useEffect(() => {
    if (!isSupported) return;
    navigator.serviceWorker
      .register(SW_PATH)
      .then((reg) => {
        // Wait for the service worker to be active
        if (reg.active) { setSwReady(true); return; }
        const sw = reg.installing || reg.waiting;
        if (sw) {
          sw.addEventListener("statechange", () => {
            if (sw.state === "activated") setSwReady(true);
          });
        }
      })
      .catch(() => {
        // SW registration failed (local dev / HTTPS required)
        setSwReady(false);
      });
  }, [isSupported]);

  // Request browser notification permission + subscribe to push
  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!isSupported || !user) return false;
    setSubscribing(true);

    try {
      // 1. Ask for permission
      const result = await Notification.requestPermission();
      setPermission(result as NotifPermission);
      if (result !== "granted") { setSubscribing(false); return false; }

      // 2. If VAPID key is set up, subscribe to Web Push (closed-tab notifications)
      if (VAPID_PUBLIC_KEY && swReady) {
        const reg = await navigator.serviceWorker.ready;
        // Check if already subscribed
        const existing = await reg.pushManager.getSubscription();
        if (!existing) {
          const sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
          });
          // Store subscription in Supabase
          await supabase.from("push_subscriptions").upsert(
            { user_id: user.id, subscription: sub.toJSON() },
            { onConflict: "user_id" }
          );
        }
      }

      setSubscribing(false);
      return true;
    } catch {
      setSubscribing(false);
      return false;
    }
  }, [isSupported, user, swReady]);

  return {
    permission,
    isSupported,
    swReady,
    subscribing,
    requestPermission,
    showLocalNotification,
  };
}
